/**
 * Batch Service
 * Handles API calls for batch management
 */

import { Injectable, inject } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { BatchManagementService } from '@app/api/stock-service/api/batchManagement.service';
import { BatchCreateRequestDTO } from '@app/api/stock-service/model/batchCreateRequestDTO';
import { BatchInfoVO } from '@app/api/stock-service/model/batchInfoVO';
import { BatchPageQueryDTO } from '@app/api/stock-service/model/batchPageQueryDTO';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import {
  Batch,
  BatchCreateRequest,
  BatchSearchParams,
} from '../models/batch.interface';
import { GoodsSkuControllerService } from '@app/api/api/goodsSkuController.service';

@Injectable({
  providedIn: 'root',
})
export class BatchService {
  private readonly batchManagementService = inject(BatchManagementService);
  private readonly goodsSkuController = inject(GoodsSkuControllerService);

  /**
   * Get list of batches with pagination and filters
   */
  getBatches(
    params: BatchSearchParams & { page: number; size: number },
  ): Observable<{ content: Batch[]; totalElements: number; number: number }> {
    const queryDTO: BatchPageQueryDTO = {
      page: params.page,
      size: params.size,
      sortBy: 'creationDate',
      ascending: false,
      ...(params.skuCode?.trim() && { skuCode: params.skuCode }),
      ...(params.status && { status: params.status }),
      ...(params.warehouseId && { warehouseId: params.warehouseId }),
    };

    return this.batchManagementService
      .page7(queryDTO, 'body', false, { httpHeaderAccept: '*/*' })
      .pipe(
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response);
        }),
        map((response: any) => {
          const pageContent = BlobConverterUtil.extractPageContent(
            response,
            [],
          );
          const totalElements = BlobConverterUtil.extractTotalElements(
            response,
            0,
          );

          const batches: Batch[] = pageContent.map((item: BatchInfoVO) =>
            this.mapBatchInfoVOToBatch(item),
          );

          return {
            content: batches,
            totalElements: totalElements,
            number: response.number || params.page,
          };
        }),
        catchError((error) => {
          throw error;
        }),
      );
  }

  /**
   * Create a new batch
   */
  createBatch(request: BatchCreateRequest): Observable<Batch> {
    const createDTO: BatchCreateRequestDTO = {
      batchId: request.batchId,
      skuCode: request.skuCode,
      expiryDate: request.expiryDate ?? undefined,
    };

    return this.batchManagementService
      .create5(createDTO, 'body', false, { httpHeaderAccept: '*/*' })
      .pipe(
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response);
        }),
        map((response: BatchInfoVO) => this.mapBatchInfoVOToBatch(response)),
        catchError((error) => {
          throw error;
        }),
      );
  }

  /**
   * Get all SKU options (no limit, for initial page load)
   */
  getAllSkuOptions(): Observable<
    Array<{ id: number; code: string; name: string }>
  > {
    return this.goodsSkuController
      .getList3(
        undefined, // spuName
        undefined, // spuCode
        undefined, // spuType
        0, // setFlag (ordinary SKUs)
        undefined, // categoryIdList
        [3], // status: 3-Effective (active SKUs)
        undefined, // skuName
        undefined, // skuCode
        0, // page
        10000, // large size to get all SKUs
        undefined, // sortBy
        undefined, // ascending
      )
      .pipe(
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response);
        }),
        map((response: any) => {
          const pageContent = BlobConverterUtil.extractPageContent(
            response,
            [],
          );
          return pageContent.map((item: any) => ({
            id: item.id || 0,
            code: item.skuCode || '',
            name: item.skuName || '',
          }));
        }),
        catchError(() => of([])),
      );
  }


  updateExpiry(id: number, expiryDate: string): Observable<Batch> {
    return this.batchManagementService.updateExpiry(id, { expiryDate }).pipe(
      switchMap((response: any) =>
        BlobConverterUtil.isBlob(response)
          ? from(BlobConverterUtil.convertToJson(response))
          : of(response),
      ),
      map((response: BatchInfoVO) => this.mapBatchInfoVOToBatch(response)),
    );
  }

  updateStatus(id: number, status: 'ACTIVE' | 'INACTIVE'): Observable<Batch> {
    return this.batchManagementService.updateStatus(id, { status }).pipe(
      switchMap((response: any) =>
        BlobConverterUtil.isBlob(response)
          ? from(BlobConverterUtil.convertToJson(response))
          : of(response),
      ),
      map((response: BatchInfoVO) => this.mapBatchInfoVOToBatch(response)),
    );
  }

  /**
   * Map BatchInfoVO to Batch interface
   */
  private mapBatchInfoVOToBatch(vo: BatchInfoVO): Batch {
    return {
      id: vo.id,
      batchId: vo.batchId || '',
      skuId: 0,
      skuCode: vo.skuCode,
      skuName: vo.skuName,
      lotValue: vo.lotValue,
      manufactureDate: vo.manufactureDate
        ? new Date(vo.manufactureDate)
        : undefined,
      expiryDate: vo.expiryDate ? new Date(vo.expiryDate) : undefined,
      qty: vo.qty || 0,
      warehouseId: vo.warehouseId,
      warehouseName: vo.warehouseName,
      status: (vo.status as Batch['status']) || 'ACTIVE',
    };
  }
}
